import React from 'react';
import { CustomHits } from '../../../components/CustomHits';
import { NoResults } from '../../../components/NoResults';
import type { ViewMode } from '../hooks/useViewMode';

export interface SearchResultsProps {
  viewMode: ViewMode;
  /** Kept for callers wiring up the shared view-mode toggle in SearchSidebar; not read here. */
  setViewMode?: (mode: ViewMode) => void;
  hasNoResults: boolean;
  /** True when hasNoResults is standing in for a search-backend error, not a genuine zero-hit search. */
  isBackendUnavailable?: boolean;
  query?: string;
  className?: string;
  /** Rendered in-feed after the 3rd (mobile) / 6th (desktop) card — e.g. the top-employers strip. */
  interstitial?: React.ReactNode;
  /** Rendered 2 grid rows after `interstitial` in each layout — e.g. the top-roles strip. */
  secondInterstitial?: React.ReactNode;
  /** Map mode only: floated over the top of the map pane instead of pushed above it in normal
   * document flow — e.g. the SRP's "get email alerts" CTA. See MapView.tsx's `mapOverlay` prop. */
  mapOverlay?: React.ReactNode;
}

/**
 * Search results component - displays CustomHits or NoResults based on state.
 */
export const SearchResults: React.FC<SearchResultsProps> = React.memo(({
  viewMode,
  setViewMode,
  hasNoResults,
  isBackendUnavailable = false,
  query,
  className = '',
  interstitial = null,
  secondInterstitial = null,
  mapOverlay = null,
}) => {
  if (hasNoResults && viewMode !== 'map') {
    return (
      <main id="job-results" className={`job-results ${className}`}>
        <NoResults query={query} isBackendUnavailable={isBackendUnavailable} />
      </main>
    );
  }

  return (
    <main id="job-results" className={`job-results ${className}`}>
      <CustomHits viewMode={viewMode} setViewMode={setViewMode} interstitial={interstitial} secondInterstitial={secondInterstitial} mapOverlay={mapOverlay} />
    </main>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.hasNoResults === nextProps.hasNoResults &&
    prevProps.isBackendUnavailable === nextProps.isBackendUnavailable &&
    prevProps.query === nextProps.query &&
    prevProps.className === nextProps.className &&
    prevProps.interstitial === nextProps.interstitial &&
    prevProps.secondInterstitial === nextProps.secondInterstitial &&
    prevProps.mapOverlay === nextProps.mapOverlay
  );
});
